import { Component, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Event, NavigationEnd, Router, RouterEvent } from '@angular/router';

import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { DropdownObject, SearchBox_onChangeOverrideEvent } from '@inmoment/the-kitchen';

import { HasTitle } from 'app/has-title';
import { AnalyticsService, TimerFn } from 'app/providers/analytics.service';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import {
  RealtimeConnection,
  RealtimeMessage,
  RealtimeService,
} from 'app/providers/realtime.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { StorageListResult } from 'app/providers/storage-api.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { LocalStorageKey, LocalStorageService } from 'app/services/local-storage.service';
import { LoggerService } from 'app/services/logger.service';
import { NavbarService } from 'app/services/navbar.service';

import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { RtrEventHandlerService } from '../../../../services/rtr-event-handler.service';
import { ProjectCreateDialogComponent } from '../../components/project-create/project-create.component';
import { ProjectListHandlerService } from './project-list-handler.service';

enum SortTypes {
  DATE = 1,
  NAME = 2,
}

@EnableClassTelemetry()
@Component({
  selector: 'app-projects',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss'],
  standalone: false,
})
export class ProjectListComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild(MatMenuTrigger, { static: true }) sortMenuTrigger: MatMenuTrigger;

  public isLoading = true;

  public MAX_PROJECT_COUNT = 100;

  public currentSort = SortTypes.DATE;
  public reverseSort = true;

  public projectsSort = '1';

  public allProjects: StorageListResult<Project> = {};
  public projectList: Project[] = [];
  public filteredProjects: Project[] = [];

  public selectedProject: Project;

  public projectMenu: Project | null = null;

  public projectsListSortData: DropdownObject[] = [
    { title: '', id: '1' },
    { title: '', id: '2' },
    { title: '', id: '3' },
    { title: '', id: '4' },
  ];

  public projectsRightPlaceholder = {
    title: '',
    subtitle: '',
  };

  private session: Session;
  isExecutive: boolean;
  createProjectTooltip: string;
  canCreateProject: boolean;
  showCreateProject: boolean;
  realtimeHandlers: RealtimeConnection[] = [];
  reportTiming: TimerFn;
  groupId: string;
  //Side panel
  isPanelOpen: boolean;

  documentCountChange$ = new Subject<
    RealtimeMessage<{ id: string; projectId: string; collectionId: string }>
  >();
  private unsub$ = new ReplaySubject<void>(1);
  public projectListHandlerService = inject(ProjectListHandlerService);

  constructor(
    protected titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private dialogService: DialogService,
    private projectService: ProjectService,
    private collectionService: CollectionService,
    private storageAccountService: StorageAccountService,
    private preferencesService: SemantriaPreferences,
    private authService: AuthService,
    private groupService: GroupService,
    private googleAnalyticsService: AnalyticsService,
    private navbarService: NavbarService,
    private localStorageService: LocalStorageService,
    private translocoService: TranslocoService,
    private ngZone: NgZone,
    private rtrHandlerService: RtrEventHandlerService,
    private logger: LoggerService
  ) {
    super(titleService);

    this.reportTiming = this.googleAnalyticsService.createTimer('Projects');
    const params = this.route.snapshot.paramMap;
    this.groupId = params.get('groupId');

    this.isPanelOpen = this.localStorageService.getItem(LocalStorageKey.PROJECT_PANEL_OPEN) ?? true;
  }

  async ngOnInit() {
    this.setTitle('Projects');

    this.router.events
      .pipe(
        filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationEnd),
        takeUntil(this.unsub$)
      )
      .subscribe((e: RouterEvent) => {
        if (e.url === this.getProjectsListRoute()) {
          this.autoSelectProject();
        }
      });
    this.documentCountChange$
      .pipe(takeUntil(this.unsub$), debounceTime(1000), distinctUntilChanged())
      .subscribe((value) => this.handleCollectionsEvent(value));

    try {
      const [_, session, preferences] = await Promise.all([
        this.groupService.refreshAndSelectActiveGroup(this.groupId),
        this.authService.getSession(),
        this.preferencesService.getPreferences(),
      ]);
      this.session = session;
      this.isExecutive = this.authService.isExecutive;
      const storedSorting = preferences.projectSorting;

      if (storedSorting) {
        this.currentSort = Math.abs(storedSorting);
        this.reverseSort = Math.sign(storedSorting) > 0;
      }
      this.populateStoredSortValue();
      await this.refresh();
      this.reportTiming('projects_loaded');

      this.canCreateProject = this.projectList.length < this.MAX_PROJECT_COUNT;
      this.showCreateProject = !this.authService.isReadOnly;
      const params = this.route.firstChild?.snapshot.paramMap;
      if (!params || !params.get('projectId')) {
        this.autoSelectProject();
      }

      this.route.firstChild?.params.subscribe((routeParams) => {
        const project = this.projectList.find((p) => {
          return p.id === routeParams.projectId;
        });
        if (project) {
          this.onProjectSelected(project);
        } else {
          this.autoSelectProject();
        }
      });
    } catch (error) {
      this.logger.error('project-list ngOnInit error', { error });
    }

    this.translocoService
      .selectTranslateObject('project-list')
      .pipe(takeUntil(this.unsub$))
      .subscribe((t) => {
        this.projectsRightPlaceholder = {
          title: t['no-projects-available'],
          subtitle: this.showCreateProject ? t['create-project-navigation'] : '',
        };
        this.internalProps.searchBox.placeholder = t['search-by-project-name'];
        this.projectsListSortData = [
          { title: t['sort-by-date-ascending'], id: '1' },
          { title: t['sort-by-date-descending'], id: '2' },
          { title: t['sort-by-name-ascending'], id: '3' },
          { title: t['sort-by-name-descending'], id: '4' },
        ];
        this.createProjectTooltip = this.canCreateProject
          ? t['create-new-project']
          : t['maximum-projects'];
      });
  }

  private autoSelectProject() {
    const lastVisitedProject = this.localStorageService.getItem('lastProjectVisited');
    if (
      lastVisitedProject &&
      lastVisitedProject.groupId === this.groupId &&
      lastVisitedProject.projectId
    ) {
      const project = this.projectList.find((p) => {
        return p.id === lastVisitedProject.projectId;
      });
      if (
        project &&
        project.status !== 'DELETION_REQUESTED' &&
        project.status !== 'DELETION_IN_PROGRESS'
      ) {
        this.onProjectSelected(project);
        return;
      }
    }
    if (this.projectList.length > 0) {
      const project = this.filteredProjects.find(
        (p) => p.status !== 'DELETION_REQUESTED' && p.status !== 'DELETION_IN_PROGRESS'
      );
      this.onProjectSelected(project);
    }
  }

  private populateStoredSortValue() {
    if (this.currentSort === SortTypes.DATE) {
      if (this.reverseSort === false) {
        this.projectsSort = '1';
      } else if (this.reverseSort === true) {
        this.projectsSort = '2';
      }
    } else if (this.currentSort === SortTypes.NAME) {
      if (this.reverseSort === false) {
        this.projectsSort = '3';
      } else if (this.reverseSort === true) {
        this.projectsSort = '4';
      }
    }
  }

  onProjectSelected(project: Project, event: any = undefined) {
    if (!project) {
      return;
    }
    // Allow re-selecting the current project when clicking the Projects menu on the list route
    // so the view can reinitialize instead of leaving the content blank.
    const projectsListUrl = this.getProjectsListRoute();
    const isSameProject: boolean = this.selectedProject?.id === project.id;
    const isOnProjectList: boolean = this.router.url === projectsListUrl;
    if (isSameProject && !isOnProjectList) {
      return;
    }
    const url = `${projectsListUrl}/${project.id}`;
    if (event?.metaKey || event?.detail?.metaKey) {
      window.open(url, '_blank');
      return;
    }
    this.realtimeHandlers.forEach((handler) => {
      if (handler) {
        handler.disconnect();
      }
    });
    this.realtimeHandlers = [
      this.projectService.on(project.id, this.handleProjectEvent.bind(this)),
      this.collectionService.on(project.id, this.onHandleCollectionsEvent.bind(this)),
    ];

    this.selectedProject = project;
    this.projectListHandlerService.shouldListBeDisabled.set(true);
    if (this.router.url !== url && !this.router.url.startsWith(`${url};`)) {
      this.router.navigate([url]);
    }
    const lastProjectVisited = { groupId: this.groupId, projectId: project.id };
    this.localStorageService.setItem('lastProjectVisited', lastProjectVisited);
  }

  private getProjectsListRoute(): string {
    return `/groups/${this.groupId}/projects`;
  }

  ngOnDestroy() {
    this.unsub$.next();
    this.unsub$.complete();
    this.realtimeHandlers.forEach((handler) => {
      if (handler) {
        handler.disconnect();
      }
    });
  }

  private handleProjectEvent(data: RealtimeMessage<Project>) {
    if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
      const idx = this.allProjects.items.findIndex((p) => p.id === data.object.id);
      if (idx !== -1) {
        this.allProjects.items[idx] = data.object;
      } else {
        this.allProjects.items.push(data.object);
        this.allProjects.count += 1;
      }
    } else if (data.verb === 'DELETE') {
      const idx = this.allProjects.items.findIndex((p) => p.id === data.object.id);
      if (idx !== -1) {
        this.allProjects.items.splice(idx, 1);
        this.allProjects.count -= 1;
      }
    }

    this.setCurrentProjects();
    this.projectListComponentToolTip();
  }

  private onHandleCollectionsEvent(
    data: RealtimeMessage<{ id: string; projectId: string; collectionId: string }>
  ) {
    // This may couse some issues that the data changed
    this.documentCountChange$.next(data);
  }

  private handleCollectionsEvent(data: RealtimeMessage<{ id: string; projectId: string }>) {
    const projectIdx = this.allProjects.items.findIndex((p) => p.id === data.object.projectId);
    if (projectIdx > -1) {
      const project = this.allProjects.items[projectIdx];

      const collectionIdx = project.collections.findIndex(
        (c) => c.collectionName === data.object.id
      );
      if (data.verb === 'DELETE') {
        if (collectionIdx !== -1) {
          project.collections.splice(collectionIdx, 1);
          project.collectionCount = project.collections.length;
        }
      } else if (
        (data.verb === 'UPDATE' && !RealtimeService.isLogicallyDeleted(data)) ||
        data.verb === 'CREATE'
      ) {
        this.rtrHandlerService.handleCollectionRtrEvent(data).then((updatedCollection) => {
          const innerCollectionIdx = project.collections.findIndex(
            (c) => c.collectionName === updatedCollection.collectionName
          );
          if (innerCollectionIdx === -1) {
            project.collections.push(updatedCollection);
          } else {
            project.collections[innerCollectionIdx] = updatedCollection;
          }
          let projectDocumentCount = 0;
          if (project.collections) {
            for (const collection of project.collections) {
              projectDocumentCount += collection.documentCount;
            }
          }
          project.documentCount = projectDocumentCount;
        });
      }
      this.projectListComponentToolTip();
    }
  }

  public async refresh(): Promise<void> {
    this.isLoading = true;
    // TODO: Document count should be somehow updated
    this.allProjects = await this.projectService.searchProjects(
      null,
      this.MAX_PROJECT_COUNT,
      true,
      true,
      this.isExecutive
    );
    this.allProjects.items = this.allProjects.items.filter(
      (p) => p.status !== 'DELETION_REQUESTED' && p.status !== 'DELETION_IN_PROGRESS'
    );

    this.setCurrentProjects();
    this.projectListComponentToolTip();
    this.isLoading = false;
  }

  public setCurrentProjects(): void {
    this.sortProjects(this.allProjects.items);
    this.projectList = this.allProjects.items;
    this.filteredProjects = [...this.projectList];
    const params = this.route.firstChild?.snapshot.paramMap;
    if (params) {
      const projectId = params.get('projectId');
      const project = this.projectList.find((p) => {
        return p.id === projectId;
      });
      if (this.selectedProject?.id !== projectId) {
        this.onProjectSelected(project);
      }
    }
  }

  public createProject(): void {
    this.dialogService
      .showDialog<ProjectCreateDialogComponent>(ProjectCreateDialogComponent, {}, {}, false)
      .afterClosed()
      .subscribe(async (response) => {
        if (response) {
          this.session = await this.authService.getSession();
          this.refresh();
        }
      });
  }

  public onSortProjectsList(id: string) {
    this.projectsSort = id;

    switch (id) {
      case '1':
        this.setSortType(SortTypes.DATE, false);
        break;
      case '2':
        this.setSortType(SortTypes.DATE, true);
        break;
      case '3':
        this.setSortType(SortTypes.NAME, false);
        break;
      case '4':
        this.setSortType(SortTypes.NAME, true);
        break;
    }
  }

  public setSortType(sortType: number, reverseSort: boolean) {
    this.currentSort = sortType;
    this.reverseSort = reverseSort;

    this.preferencesService
      .savePreference('projectSorting', this.currentSort * (this.reverseSort ? 1 : -1))
      .catch((reason) => {
        this.alertService.showErrorToast(
          this.translocoService.translate('project-list.project-sorting-preference-failed-to-save'),
          reason
        );
      });
    this.sortProjects(this.filteredProjects);
  }

  public sortProjects(filteredProjects: Project[]) {
    switch (this.currentSort) {
      case SortTypes.DATE:
        filteredProjects.sort((a, b) => cmp(a.modifiedAt, b.modifiedAt, this.reverseSort));
        break;
      case SortTypes.NAME:
        filteredProjects.sort((a, b) => cmp(a.name, b.name, this.reverseSort));
        break;
    }
  }

  public get isReadOnly() {
    return this.authService.isReadOnly;
  }

  async projectListComponentToolTip() {
    const accountCounts = await this.storageAccountService.getAccountCounts();
    if (accountCounts) {
      const projectCount = accountCounts.projectsCount
        ? accountCounts.projectsCount.toLocaleString()
        : 0;
      const projectLimit = accountCounts.projectsLimit
        ? accountCounts.projectsLimit.toLocaleString()
        : 0;
      const accountDocumentCount = accountCounts.documentCount
        ? accountCounts.documentCount.toLocaleString()
        : 0;
      const accountDocumentLimit = accountCounts.documentLimit
        ? accountCounts.documentLimit.toLocaleString()
        : 0;
      const accountRemainingCredits = accountCounts.semantriaCredits
        ? accountCounts.semantriaCredits.toLocaleString()
        : 0;
      const tooltip = this.translocoService.translate('project-list.account-counts', {
        projectCount,
        projectLimit,
        accountDocumentCount,
        accountDocumentLimit,
        accountRemainingCredits,
      });
      this.navbarService.projectListComponentTooltip(tooltip);
    }

    return '';
  }

  onPanelStateChange(isOpen: boolean) {
    this.localStorageService.setItem(LocalStorageKey.PROJECT_PANEL_OPEN, isOpen);
  }

  public internalProps = {
    searchBox: {
      placeholder: '',
      onChangeOverride: ($event: SearchBox_onChangeOverrideEvent) => {
        this.ngZone.run(() => {
          this.filteredProjects = this.projectList.filter(
            (s) => s.name.toLocaleLowerCase().indexOf($event.searchText?.toLocaleLowerCase()) >= 0
          );
        });
      },
    },
  };
}

function cmp(a: any, b: any, reverse = false): number {
  if (a === b) {
    return 0;
  }

  let order = 0;
  if (typeof a === 'string') {
    order = a.localeCompare(b);
  } else {
    order = a > b ? 1 : -1;
  }

  if (reverse) {
    order *= -1;
  }
  return order;
}
